/**
 * DriveFilePanel — the "Drive" side tab.
 *
 * The Google Drive file the agent is editing, in Google's OWN editor
 * (embedded; the user's Google session renders it, live), with a slim
 * version bar at the bottom — the same idiom as Claude's artifact version
 * chevrons and ChatGPT Canvas's back/forward + "Restore": ‹ › step through
 * the agent's changes, one line per step, Undo on the step you are looking
 * at, and a clock button that unfolds the full history only when asked.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Popconfirm, message as antdMessage } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { formatRelativeTime, formatExactTime } from '@/utils/relativeTime';
import type { DriveEdit } from '@/types/models';
import { driveKind } from '../../services/driveService';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import { DriveKindIcon, ExternalLinkIcon, GoogleDriveGlyph } from './driveIcons';
import './DriveFilePanel.css';

interface Props {
  edits: DriveEdit[];
  activeFileId: string | null;
  onSelectFile: (fileId: string) => void;
  onRevert: (editId: string) => Promise<void>;
  onResize: (width: number) => void;
}

const MIN_W = 360;
const MAX_W = 1100;

function Chevron({ dir }: { dir: 'prev' | 'next' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {dir === 'prev' ? <path d="m15 6-6 6 6 6" /> : <path d="m9 6 6 6-6 6" />}
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  );
}

export function DriveFilePanel({ edits, activeFileId, onSelectFile, onRevert, onResize }: Props) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cursor, setCursor] = useState<number | null>(null);   // null = latest step
  const [iframeKey, setIframeKey] = useState(0);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  const files = useMemo(() => {
    const seen = new Map<string, DriveEdit>();
    for (const e of edits) seen.set(e.file_id, e);   // last write wins → latest name/link
    return Array.from(seen.values());
  }, [edits]);
  const active = files.find((f) => f.file_id === activeFileId) || files[files.length - 1];
  // Only real edits are steps. A file the agent merely opened (`shown`) has
  // a tab above but nothing to page through and nothing to undo below.
  const steps = useMemo(
    () => edits.filter((e) => e.file_id === active?.file_id && !e.shown),
    [edits, active?.file_id],
  );

  // A new step (or an undo) always shows the latest and refreshes the editor
  // frame, so a viewer whose Google session does not push live updates still
  // sees the current state.
  const lastStepId = steps.length ? steps[steps.length - 1].edit_id : '';
  const undoneCount = steps.filter((s) => s.reverted).length;
  useEffect(() => { setCursor(null); setIframeKey((k) => k + 1); }, [lastStepId, undoneCount, active?.file_id]);

  useEffect(() => {
    if (!historyOpen) return;
    const onDown = (e: MouseEvent) => { if (historyRef.current && !historyRef.current.contains(e.target as Node)) setHistoryOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setHistoryOpen(false); };
    document.addEventListener('mousedown', onDown); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [historyOpen]);

  const onHandleDown = (e: React.PointerEvent) => {
    dragRef.current = { startX: e.clientX, startW: panelRef.current?.getBoundingClientRect().width || 480 };
    const move = (ev: PointerEvent) => {
      if (!dragRef.current) return;
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const delta = (dragRef.current.startX - ev.clientX) * (rtl ? -1 : 1);
      onResize(Math.max(MIN_W, Math.min(MAX_W, dragRef.current.startW + delta)));
    };
    const up = () => { dragRef.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const revert = async (editId: string) => {
    setBusy(true);
    try {
      await onRevert(editId);
      setHistoryOpen(false);
      antdMessage.success(t('chat.drive.undone'));
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || t('chat.drive.undoFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (!active) return null;
  const link = safeHttpUrl(active.link);
  const embed = safeHttpUrl(active.embed_url);
  const total = steps.length;
  const idx = cursor === null ? total - 1 : Math.max(0, Math.min(total - 1, cursor));
  const step = total ? steps[idx] : null;
  const summary = step ? step.summary.join(' · ') : '';

  return (
    <div className="drive-panel" ref={panelRef}>
      <div className="drive-panel__resize" role="separator" aria-orientation="vertical" onPointerDown={onHandleDown}>
        <span className="drive-panel__resize-grip" aria-hidden="true" />
      </div>

      <header className="drive-panel__head">
        {files.length > 1 ? (
          <div className="drive-panel__files" role="tablist">
            {files.map((f) => (
              <button key={f.file_id} type="button" role="tab" aria-selected={f.file_id === active.file_id}
                className={`drive-panel__file ${f.file_id === active.file_id ? 'is-active' : ''}`}
                onClick={() => onSelectFile(f.file_id)} title={f.name}>
                <DriveKindIcon kind={driveKind(f.mime_type, f.name)} size={16} />
                <span dir="auto">{f.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="drive-panel__title">
            <DriveKindIcon kind={driveKind(active.mime_type, active.name)} size={18} />
            <span className="drive-panel__name" dir="auto" title={active.name}>{active.name}</span>
          </div>
        )}
        {link && (
          <a className="drive-panel__open" href={link} target="_blank" rel="noopener noreferrer" title={t('chat.drive.openLive')}>
            <GoogleDriveGlyph size={13} />
            <ExternalLinkIcon size={12} />
          </a>
        )}
      </header>

      <div className="drive-panel__live">
        {embed ? (
          <iframe key={iframeKey} src={embed} title={active.name} allow="clipboard-write" referrerPolicy="strict-origin-when-cross-origin" />
        ) : (
          <div className="drive-panel__nolive">{t('chat.drive.liveHint')}</div>
        )}
      </div>

      {/* Version bar — one slim line: ‹ › step N of M · what changed · Undo · history */}
      {step && (
        <div className="drive-panel__bar" ref={historyRef}>
          <button type="button" className="drive-panel__nav" onClick={() => setCursor(idx - 1)} disabled={idx <= 0} aria-label={t('chat.drive.prevStep')} title={t('chat.drive.prevStep')}>
            <Chevron dir="prev" />
          </button>
          <button type="button" className="drive-panel__nav" onClick={() => setCursor(idx + 1 >= total - 1 ? null : idx + 1)} disabled={idx >= total - 1} aria-label={t('chat.drive.nextStep')} title={t('chat.drive.nextStep')}>
            <Chevron dir="next" />
          </button>
          <span className="drive-panel__pos" title={formatExactTime(step.at)}>
            {t('chat.drive.stepOf', { n: idx + 1, total })}
          </span>
          <span className={`drive-panel__what ${step.reverted ? 'is-reverted' : ''}`} title={step.summary.join('\n')}>
            {summary}
          </span>
          {step.reverted ? (
            <span className="drive-panel__tag">{t('chat.drive.undone')}</span>
          ) : (
            <Popconfirm
              title={t('chat.drive.undoConfirm', { name: active.name })}
              okText={t('chat.drive.undo')}
              cancelText={t('common.cancel')}
              onConfirm={() => void revert(step.edit_id)}
            >
              <button type="button" className="drive-panel__undo" disabled={busy}>{busy ? '…' : t('chat.drive.undo')}</button>
            </Popconfirm>
          )}
          <button type="button" className={`drive-panel__nav drive-panel__history-btn ${historyOpen ? 'is-open' : ''}`} onClick={() => setHistoryOpen((o) => !o)} aria-expanded={historyOpen} aria-label={t('chat.drive.history')} title={t('chat.drive.history')}>
            <ClockIcon />
          </button>

          {historyOpen && (
            <div className="drive-panel__history" role="listbox" aria-label={t('chat.drive.history')}>
              <div className="drive-panel__history-head">
                <span>{t('chat.drive.steps')}</span>
                <span className="drive-panel__history-hint">{t('chat.drive.versionHistory')}</span>
              </div>
              {steps.map((s, i) => (
                <button key={s.edit_id} type="button" role="option" aria-selected={i === idx}
                  className={`drive-panel__hrow ${i === idx ? 'is-current' : ''} ${s.reverted ? 'is-reverted' : ''}`}
                  onClick={() => { setCursor(i === total - 1 ? null : i); setHistoryOpen(false); }}>
                  <span className="drive-panel__hn">{i + 1}</span>
                  <span className="drive-panel__htext">{s.summary.join(' · ')}</span>
                  <span className="drive-panel__hwhen" title={formatExactTime(s.at)}>{s.reverted ? t('chat.drive.undone') : formatRelativeTime(s.at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
